import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Copy, Plus, Trash2, Router as RouterIcon, Network as NetworkIcon } from 'lucide-react';
import { Container, PageHeader, Section, Toolbar, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import { StatusPill, DataTable, EmptyState, type ColumnDef, type StatusTone } from '@vnetwork/ui/data';
import { Button, IconButton, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { formatDate } from '@/lib/format-date';
import {
  useVirtualRouter,
  useDetachRouterNetwork,
  useNetworkList,
  type RouterNic,
} from '@/api/opennebula';
import { NetworkLeasedCell, ConnectedVmsCell } from './networking-parts';
import { RouterAttachNetworkDialog } from './RouterAttachNetworkDialog';

/** Maps router lifecycle status to a display tone. */
function routerTone(status?: string): StatusTone {
  const v = (status ?? '').toLowerCase();
  if (v === 'active' || v === 'running') return 'success';
  if (/error|fail/.test(v)) return 'danger';
  if (/pend|deploy|creat/.test(v)) return 'warning';
  return 'muted';
}

/**
 * Virtual-router detail page (routed, mirrors the legacy VRN detail): summary
 * (status, external NAT, created), attached networks (attach/detach) and static
 * routes (add/delete). Reached at `…/networks/routers/:routerId`.
 */
export function RouterDetail() {
  const navigate = useNavigate();
  const { serviceId, routerId } = useParams<{ serviceId: string; routerId: string }>();
  const { show } = useToast();
  const base = `/services/compute/${serviceId}`;

  const { data: router, isLoading } = useVirtualRouter(serviceId, routerId);
  const nics = router?.nics ?? [];

  // A router NIC carries the network's numeric OpenNebula id, but the per-network
  // detail endpoint (leased/size) is keyed by the network's UUID — so resolve
  // numeric uid → uuid via the networks list before fetching the summary.
  const { data: networks = [] } = useNetworkList(serviceId);
  const uidToUuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of networks) if (n.uid != null) m.set(String(n.uid), String(n.id));
    return m;
  }, [networks]);

  const [attachOpen, setAttachOpen] = useState(false);
  // NIC pending detach — non-null opens the confirm dialog.
  const [detachNic, setDetachNic] = useState<RouterNic | null>(null);

  const detach = useDetachRouterNetwork(serviceId!, routerId!, {
    onSuccess: () => { show({ tone: 'success', title: 'Network detached' }); setDetachNic(null); },
    onError: () => show({ tone: 'danger', title: 'Detach failed' }),
  });

  function copy(value: string) {
    navigator.clipboard?.writeText(value);
    show({ tone: 'success', title: 'Copied to clipboard' });
  }

  const nicColumns: ColumnDef<RouterNic, unknown>[] = [
    {
      id: 'name',
      header: 'Network',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.network_name || row.original.network_id}</span>,
    },
    {
      id: 'ip',
      header: 'IP address',
      cell: ({ row }) => row.original.ip
        ? <code style={{ color: 'var(--vn-fg-muted)' }}>{row.original.ip}</code>
        : <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.is_public ? 'Public' : 'Private'}</span>,
    },
    {
      // Leased/size comes from the per-network detail (not the router payload); the
      // public NIC carries the external NAT IP, not a private subnet, so skip it.
      id: 'leased',
      header: 'Address leased',
      cell: ({ row }) => {
        // NIC.network_id is the numeric OpenNebula id; the summary endpoint needs the UUID.
        const uuid = uidToUuid.get(String(row.original.network_id));
        return row.original.is_public || !uuid ? (
          <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
        ) : (
          <NetworkLeasedCell serviceId={serviceId!} networkId={uuid} />
        );
      },
    },
    {
      // Which VMs sit on this network — clickable chips to each instance (mirrors @console).
      id: 'vms',
      header: 'Connected VMs',
      cell: ({ row }) => <ConnectedVmsCell serviceId={serviceId!} instances={row.original.instances} />,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          {/* The public NIC carries the external NAT IP — it can't be detached. */}
          <IconButton variant="ghost" size="sm" aria-label="Detach network" disabled={row.original.is_public || detach.isPending} onClick={() => setDetachNic(row.original)}>
            <Trash2 size={15} />
          </IconButton>
        </div>
      ),
    },
  ];

  const networksPath = `${base}/networks`;
  const crumbs = (current: string) => (
    <Breadcrumbs>
      <BreadcrumbItem href={networksPath} onClick={(e) => { e.preventDefault(); navigate(networksPath); }}>Networking</BreadcrumbItem>
      <BreadcrumbItem current>{current}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 28, width: 260, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 120 }} />
      </Container>
    );
  }
  if (!router) {
    return (
      <Container size="xl">
        <PageHeader breadcrumbs={crumbs('Router')} icon={<RouterIcon size={22} />} title="Router not found" description="This virtual router doesn't exist or is no longer available." />
        <Button variant="secondary" onClick={() => navigate(networksPath)}>Back to networking</Button>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={crumbs(router.name || routerId || 'Router')}
        icon={<RouterIcon size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-3)', minWidth: 0 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{router.name || routerId}</span>
            <StatusPill tone={routerTone(router.status)}>{router.status || 'Unknown'}</StatusPill>
          </span>
        }
        description="Manage attached networks and static routes for this virtual router."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Summary — CompactStatGroup, the console's detail-page KPI pattern
            (mirrors VpnGatewayDetail / LoadBalancerDetail). */}
        <Card flat>
          <CardBody>
            <CompactStatGroup columns={3}>
              <CompactStat
                label="External NAT"
                value={
                  router.public_ip ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
                      <code>{router.public_ip}</code>
                      <IconButton variant="ghost" size="sm" aria-label="Copy IP" onClick={() => copy(router.public_ip!)}><Copy size={13} /></IconButton>
                    </span>
                  ) : '—'
                }
              />
              <CompactStat label="Networks" value={nics.length} />
              <CompactStat label="Created" value={router.created_at ? formatDate(router.created_at) : '—'} />
            </CompactStatGroup>
          </CardBody>
        </Card>

        {/* NAT configuration — NAT4 / SDNAT4 state + interfaces read from the
            router's VNF context (mirrors @console's NAT Configuration card). */}
        {router.nat_context && (
          <Section title="NAT configuration" description="Network Address Translation for this router's interfaces." style={{ marginBottom: 0 }}>
            <Card flat>
              <CardBody>
                <CompactStatGroup columns={4}>
                  <CompactStat
                    label="NAT4"
                    value={<StatusPill tone={router.nat_context.nat4_enabled ? 'success' : 'muted'}>{router.nat_context.nat4_enabled ? 'Enabled' : 'Disabled'}</StatusPill>}
                  />
                  <CompactStat
                    label="SDNAT4"
                    value={<StatusPill tone={router.nat_context.sdnat4_enabled ? 'success' : 'muted'}>{router.nat_context.sdnat4_enabled ? 'Enabled' : 'Disabled'}</StatusPill>}
                  />
                  <CompactStat label="Outbound" value={router.nat_context.nat4_interfaces_out || '—'} />
                  <CompactStat label="SDNAT interfaces" value={router.nat_context.sdnat4_interfaces || '—'} />
                </CompactStatGroup>
              </CardBody>
            </Card>
          </Section>
        )}

        {/* Attached networks — Toolbar + DataTable, the console's list pattern
            (mirrors VirtualRouters / VPN ConnectionsTab). */}
        <Section title="Attached networks" description="Networks on the same router can communicate with each other." style={{ marginBottom: 0 }}>
          <Toolbar
            end={
              <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>
                Attach network
              </Button>
            }
          />
          <DataTable
            columns={nicColumns}
            data={nics}
            getRowId={(n) => String(n.nic_id)}
            emptyState={
              <EmptyState
                size="sm"
                icon={<NetworkIcon size={28} />}
                title="No networks attached"
                description="Networks on the same router can route to each other."
                actions={<Button size="sm" startIcon={<Plus size={15} />} onClick={() => setAttachOpen(true)}>Attach network</Button>}
              />
            }
          />
          <RouterAttachNetworkDialog
            serviceId={serviceId!}
            routerId={routerId!}
            attachedNetworkIds={nics.map((n) => String(n.network_id))}
            open={attachOpen}
            onOpenChange={setAttachOpen}
          />
          <ConfirmDialog
            open={!!detachNic}
            onOpenChange={(o) => !o && setDetachNic(null)}
            title="Detach network?"
            description={`Detach “${detachNic?.network_name || detachNic?.network_id}” from this router? Instances on this network will lose routes through it.`}
            confirmLabel="Detach"
            destructive
            loading={detach.isPending}
            onConfirm={() => detachNic && detach.mutate({ nic_id: detachNic.nic_id ?? '' })}
          />
        </Section>

        {/* Static routes — temporarily disabled: the upstream
            `/routers/:id/routes` endpoint currently returns 500, so the panel is
            read-only/non-functional until the backend is fixed. */}
        <Section title="Static routes" description="Route traffic to destinations via a gateway on an attached network." style={{ marginBottom: 0 }}>
          <Alert tone="info">
            Static routes are temporarily unavailable and will be re-enabled soon.
          </Alert>
        </Section>
      </div>
    </Container>
  );
}
